import React from 'react';

import { Glossary, BoardGL } from 'common/utils/glossary';
import { Button, Title, Popup, Spinner } from 'common/components';
import { SubButtonType } from 'common/components/button';
import { BoardStatus } from 'common/modules/app/board';

interface TerminalDeleteDialogProps {
  /** TerminalDeleteDialogの表示有無 */
  isDisplay: boolean;
  /** 表示するダイアログ種別を判別するためのBoardStatus */
  boardStatus: BoardStatus;
  /** グロッサリ */
  glossary: Glossary;
  /** 削除中か否か */
  isDeleteProcessing: boolean;
  /** "OKボタン"の押下処理(確認ダイアログ) */
  onClickDelete: () => void;
  /**
   * 確認ダイアログ："キャンセルボタン"の押下処理
   * 成功/失敗ダイアログ："OKボタン"の押下処理
   **/
  onClickOkClose: () => void;
}

/**
 * 端末管理画面のPresentation Component
 */
export const TerminalDeleteDialog: React.FC<TerminalDeleteDialogProps> = ({
  isDisplay,
  boardStatus,
  glossary,
  isDeleteProcessing,
  onClickDelete,
  onClickOkClose
}) => {
  const onClickDeleteProcessing = () => {
    // 削除処理中はバックグラウンドをクリックしても何もしない
  };
  const subButtonType: SubButtonType = isDeleteProcessing
    ? 'invalid'
    : 'highPriority';
  /** BoardStatusを確認して、表示内容を決定する */
  let dialogMessage = null;
  let buttonComponent = null;
  if (boardStatus === BoardStatus.TERMINAL_CONFIRMATION_DISP) {
    // 確認ダイアログ表示
    dialogMessage = (
      <Title type="title" data-test="terminalDeleteDialog-title">
        {glossary.glx(BoardGL.TerminalBoardDeleteConfirmationMS)}
      </Title>
    );
    buttonComponent = (
      <div
        className="terminalDeleteDialogButton"
        data-test="terminalDeleteDialogButton"
      >
        <Button
          type="okButton"
          subType={subButtonType}
          data-test="terminalDeleteDialog-okButton"
          onClick={isDeleteProcessing ? onClickDeleteProcessing : onClickDelete}
        >
          {isDeleteProcessing ? (
            <Spinner type="button" width={22} height={22} color="#FFFFFF" />
          ) : (
            glossary.glx(BoardGL.TerminalBoardOK)
          )}
        </Button>
        <Button
          type="cancelButton"
          subType={subButtonType}
          data-test="terminalDeleteDialog-cancelButton"
          onClick={onClickOkClose}
        >
          {glossary.glx(BoardGL.TerminalBoardCancel)}
        </Button>
      </div>
    );
  } else if (boardStatus === BoardStatus.TERMINAL_SUCCESS_DISP) {
    // 成功ダイアログ表示
    dialogMessage = (
      <Title type="titleSuccess" data-test="terminalDeleteDialog-title">
        {glossary.glx(BoardGL.TerminalBoardDeleteSuccessMS)}
      </Title>
    );
    buttonComponent = (
      <div
        className="terminalDeleteDialogButton"
        data-test="terminalDeleteDialogButton"
      >
        <Button
          type="okButton"
          data-test="terminalDeleteDialog-okButton"
          onClick={onClickOkClose}
        >
          {glossary.glx(BoardGL.TerminalBoardOK)}
        </Button>
      </div>
    );
  } else if (boardStatus === BoardStatus.TERMINAL_FAILURE_DISP) {
    // 失敗ダイアログ表示
    dialogMessage = (
      <Title type="titleFailure" data-test="terminalDeleteDialog-title">
        {glossary.glx(BoardGL.TerminalBoardDeleteFailureMS)}
      </Title>
    );
    buttonComponent = (
      <div
        className="terminalDeleteDialogButton"
        data-test="terminalDeleteDialogButton"
      >
        <Button
          type="okButton"
          data-test="terminalDeleteDialog-okButton"
          onClick={onClickOkClose}
        >
          {glossary.glx(BoardGL.TerminalBoardOK)}
        </Button>
      </div>
    );
  }

  const _component = (
    <Popup
      className="terminalDelete-popup"
      onClickBackground={
        isDeleteProcessing ? onClickDeleteProcessing : onClickOkClose
      }
      data-test="terminalDelete-popup"
      visible={isDisplay}
    >
      <div className="terminalDeleteDialog" data-test="terminalDeleteDialog">
        {dialogMessage}
        {buttonComponent}
      </div>
    </Popup>
  );
  return isDisplay ? _component : null;
};
